import { Component, ViewChild, inject, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { UsersListComponent } from '../../components/users-list/users-list.component';
import { UserFormComponent } from '../../components/user-form/user-form.component';
import { UserService } from '../../services/user.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UserEntitiesListComponent } from '../../components/user-entities-list/user-entities-list.component';
import { UserEntitiesFormComponent } from '../../components/user-entities-form/user-entities-form.component';
import { CatalogService } from '@services/data/catalog.service';
import { AuthService } from '@services/core/auth.service';
import { PermissionService } from '@services/core/permission.service';
import { FormMode } from 'app/core/constants/form-mode';
import { AppSettings } from '@services/settings/app-settings.service';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { MasterDetailNav } from 'app/core/service/utils/master-detail-nav.service';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-users-management',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        UsersListComponent,
        UserFormComponent,
        UserEntitiesListComponent,
        UserEntitiesFormComponent,
        PageHeaderComponent,
        MobileFooterComponent,
    ],
    changeDetection: ChangeDetectionStrategy.Default,
    templateUrl: './users-management.component.html',
    styleUrl: './users-management.component.scss'
})
export class UsersManagementComponent {

    @ViewChild(UsersListComponent) userList!: UsersListComponent;
    @ViewChild('entityModal') entityModal!: any;
    @ViewChild('newUser') newUser!: any;
    @ViewChild('entitiesFormPanel') entitiesFormPanel?: UserEntitiesFormComponent;

    selectedUser: any = null;
    userForm: FormGroup;
    mode = signal<FormMode>(FormMode.VIEW);
    formMode = FormMode;
    private modalRef?: NgbModalRef;
    selectedEntityId: number | null = null;
    /** Rol sobre el que se editan entidades (alcance). */
    entitiesRoleId: number | null = null;
    entitiesRoleSlug: string | null = null;
    showEntityFormPanel = false;

    private fb = inject(FormBuilder);
    private userService = inject(UserService);
    public catalogService = inject(CatalogService);
    private authService = inject(AuthService);
    readonly perm = inject(PermissionService);
    public appSettings = inject(AppSettings);
    private md = inject(MasterDetailNav);
    private readonly basePath = 'seguridad/usuarios';

    get showUserDetail(): boolean {
        return !!this.selectedUser && !this.selectedUser.isNew;
    }

    /** Lista estable de entidades activas (evita colgar ng-select en el modal). */
    activeEntities = computed(() =>
        (this.catalogService.entities() || []).filter((e: any) => e?.isActive !== false)
    );

    /** Entidades que el usuario autenticado puede asignar. */
    assignableEntities = computed(() => {
        const mine = (this.authService.sharingEntities() as any[]) || [];
        return mine.filter((e) => e?.isActive !== false);
    });

    constructor(private modalService: NgbModal,) {
        const today = new Date();
        const nextYear = new Date();
        nextYear.setFullYear(today.getFullYear() + 1);

        this.userForm = this.fb.group({
            uuid: [null],
            mail: [null, [Validators.required, Validators.email]],
            displayName: [null, [Validators.required]],
            roleSlug: [null],
            roleSlugs: [[], [Validators.required, this.minRolesValidator]],
            roleId: [null],
            roleIds: [[]],
            partyUuid: [null],
            isActive: [true],
            expiredDate: [null, [this.minTodayValidator, this.maxOneYearValidator]]
        });
        this._loadParties();
        this.md.urlChanges$.pipe(takeUntilDestroyed()).subscribe(() => this.syncFromRoute());
    }

    parties: any[] = [];
    availableParties: any[] = [];

    private _loadParties() {
        this.userService.getParties().subscribe({
            next: (res: any) => {
                const list = Array.isArray(res) ? res : (res?.data || []);
                this.parties = (list || []).map((p: any) => ({
                    uuid: p.uuid,
                    rut: p.rut,
                    names: p.names,
                    entityCode: p.entityCode,
                    assignedUserUuid: p.portalUsers?.[0]?.uuid ?? null
                }));
                this._updateAvailableParties();
            },
            error: () => {
                this.parties = [];
                this.availableParties = [];
            }
        });
    }

    private _updateAvailableParties(): void {
        const currentUuid = this.selectedUser?.uuid ?? null;
        this.availableParties = (this.parties || []).filter(
            (p: any) => !p.assignedUserUuid || p.assignedUserUuid === currentUuid
        );
    }

    isUserRole(role: any): boolean {
        const obj = typeof role === 'object' && role ? role : this._roleById(role);
        const slug = String(obj?.slug ?? obj?.name ?? role ?? '').trim().toUpperCase();
        return slug === 'USER';
    }

    private _roleById(identifier: any): any {
        if (identifier == null) return null;
        const str = String(identifier).trim().toUpperCase();
        const num = Number(identifier);
        return (this.catalogService.roles() || []).find((r: any) =>
            String(r.slug || r.name || '').trim().toUpperCase() === str ||
            (Number.isFinite(num) && Number(r.id) === num)
        ) || (this.selectedUser?.roles || []).find((r: any) =>
            String(r.slug || r.name || '').trim().toUpperCase() === str ||
            (Number.isFinite(num) && Number(r.id) === num)
        ) || null;
    }

    hasUserRole(roles?: any[]): boolean {
        const list = roles ?? this.userForm.get('roleSlugs')?.value ?? this.userForm.get('roleIds')?.value ?? [];
        return (list || []).some((r: any) => this.isUserRole(r));
    }

    isFormUserRoleOnly(): boolean {
        const list = this.userForm.get('roleSlugs')?.value || this.userForm.get('roleIds')?.value || [];
        if (!list.length) return false;
        return list.every((item: any) => this.isUserRole(this._roleById(item)));
    }

    isEntitiesRoleUser(): boolean {
        return this.isUserRole(this._roleById(this.entitiesRoleSlug || this.entitiesRoleId));
    }

    showEntitiesPanel(): boolean {
        return !this.userForm.get('isMaster')?.value;
    }

    private minRolesValidator(control: AbstractControl): ValidationErrors | null {
        const v = control.value;
        return Array.isArray(v) && v.length > 0 ? null : { required: true };
    }

    private minTodayValidator(control: AbstractControl): ValidationErrors | null {
        if (!control.value) return null;
        const selectedDate = new Date(control.value);
        const startOfYear = new Date(new Date().getFullYear(), 0, 1);
        return selectedDate < startOfYear ? { minYear: true } : null;
    }

    private maxOneYearValidator(control: AbstractControl): ValidationErrors | null {
        if (!control.value) return null;
        const selectedDate = new Date(control.value);
        const maxDate = new Date();
        maxDate.setFullYear(maxDate.getFullYear() + 1);
        maxDate.setHours(23, 59, 59, 999);
        return selectedDate > maxDate ? { maxOneYear: true } : null;
    }

    get minDateStr(): string {
        const year = new Date().getFullYear();
        return `${year}-01-01`;
    }

    get maxDateStr(): string {
        const date = new Date();
        date.setFullYear(date.getFullYear() + 1);
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    open(content, size: 'md' | 'lg' | 'xl' = 'lg') {
        this.modalRef = this.modalService.open(content, { size: size });
    }


    /*cierra los modales */
    close() {
        if (this.modalRef) {
            this.modalRef.close();
        }
    }


    onUserSelected(user: any) {
        if (!user?.uuid) return;
        if (this.md.detailSegment(this.basePath) === String(user.uuid)) {
            this.applyUser(user);
            return;
        }
        this.md.go(this.md.item(this.basePath, user.uuid));
    }

    private applyUser(user: any) {
        let expiredDate = user.expiredDate;
        if (expiredDate) {
            expiredDate = new Date(expiredDate).toISOString().split('T')[0];
        }
        const roleSlugs = this._extractRoleSlugs(user);
        const activeSlug = user.roleSlug ?? user.role?.slug ?? user.role?.name ?? roleSlugs[0] ?? null;
        this.selectedUser = {
            ...user,
            expiredDate,
            userRoleEntities: user.userRoleEntities || [],
            roleSlugs,
            roleSlug: activeSlug,
            roles: user.roles || []
        };
        this.entitiesRoleSlug = activeSlug;
        this.entitiesRoleId = user.roleId ?? this._roleById(activeSlug)?.id ?? null;
        this.mode.set(FormMode.VIEW);
        this.userForm.reset();
        this.userForm.patchValue({
            ...this.selectedUser,
            roleSlug: activeSlug,
            roleSlugs,
            roleId: this.entitiesRoleId,
            partyUuid: user.partyUuid || user.party?.uuid || null
        });
        this.userList?.selectedId.set(user.uuid);
        this._updateAvailableParties();
    }

    private syncFromRoute(retries = 12) {
        const id = this.md.detailSegment(this.basePath);
        if (!id) {
            this.selectedUser = null;
            this.mode.set(FormMode.VIEW);
            this.userList?.selectedId.set(null);
            this._updateAvailableParties();
            return;
        }
        if (String(this.selectedUser?.uuid) === id) {
            this.userList?.selectedId.set(id);
            return;
        }
        const found = this.userList?.users?.find((u) => String(u.uuid) === id);
        if (found) {
            this.applyUser(found);
            return;
        }
        if (retries > 0) setTimeout(() => this.syncFromRoute(retries - 1), 80);
    }

    selectedUserRoles(): any[] {
        const roles = this.selectedUser?.roles;
        if (Array.isArray(roles) && roles.length) {
            return roles.map((r: any) => this._roleById(r.slug ?? r.name ?? r.id) ?? r);
        }
        const slugs = this.selectedUser?.roleSlugs || [];
        if (slugs.length) {
            return slugs.map((s: string) => this._roleById(s) ?? { slug: s, displayName: s });
        }
        const active = this.selectedUser?.roleSlug ?? this.selectedUser?.roleId;
        if (active != null) {
            const found = this._roleById(active);
            return found ? [found] : [{ slug: active, displayName: String(active) }];
        }
        return [];
    }

    onEntitiesRoleChange(identifier: any) {
        this.entitiesRoleSlug = identifier != null ? String(identifier).trim().toUpperCase() : null;
        this.entitiesRoleId = this._roleById(identifier)?.id ?? null;
    }

    entitiesForSelectedRole(): any[] {
        const targetSlug = String(this.entitiesRoleSlug || '').trim().toUpperCase();
        const targetId = Number(this.entitiesRoleId);
        const codes = new Set(
            (this.selectedUser?.userRoleEntities || [])
                .filter((r: any) =>
                    (targetSlug && String(r.roleSlug || '').trim().toUpperCase() === targetSlug) ||
                    (Number.isFinite(targetId) && Number(r.roleId) === targetId)
                )
                .map((r: any) => Number(r.entityCode))
        );
        return (this.catalogService.entities() || []).filter((e: any) => codes.has(Number(e.code)));
    }

    getCodes(items: any[]): number[] {
        return (items || []).map((i) => Number(i.code ?? i)).filter((n) => Number.isFinite(n));
    }

    saveRoleEntitiesFromModal(entitiesFormComp: any) {
        const target = this.entitiesRoleSlug ?? this.entitiesRoleId;
        if (!this.selectedUser?.uuid || !target) return;
        const selected = entitiesFormComp.formUser.get('entities')?.value ?? [];
        const codes = selected.map((e: any) => Number(typeof e === 'object' ? e.code : e));
        this._persistRoleEntities(target, codes, () => this.closeEntityFormPanel());
    }

    private _persistRoleEntities(target: any, codes: number[], onDone?: () => void) {
        this.userService.saveRoleEntities(this.selectedUser.uuid, target, codes).subscribe({
            next: (res: any) => {
                this.showSuccess('Entidades del rol guardadas');
                this._applyUserResponse(res);
                onDone?.();
            },
            error: () => this.showError('No se pudieron guardar las entidades del rol')
        });
    }

    removeRoleEntity(entityCode: any) {
        const target = this.entitiesRoleSlug ?? this.entitiesRoleId;
        if (!this.selectedUser?.uuid || !target) return;
        const next = this.getCodes(this.entitiesForSelectedRole()).filter((c) => Number(c) !== Number(entityCode));
        this.confirmDeletion(() => this._persistRoleEntities(target, next));
    }

    private _applyUserResponse(res: any) {
        const roleSlugs = this._extractRoleSlugs(res);
        const activeSlug = res.roleSlug ?? res.role?.slug ?? roleSlugs[0] ?? null;
        this.selectedUser = {
            ...res,
            userRoleEntities: res.userRoleEntities || [],
            roleSlugs,
            roleSlug: activeSlug,
            roles: res.roles || []
        };
        this.entitiesRoleSlug = activeSlug;
        this.entitiesRoleId = res.roleId ?? this._roleById(activeSlug)?.id ?? null;
        this.userForm.patchValue({
            ...this.selectedUser,
            roleSlug: activeSlug,
            roleSlugs,
            roleId: this.entitiesRoleId,
            partyUuid: res.partyUuid || res.party?.uuid || null
        });
        this._updateAvailableParties();
    }

    private _extractRoleSlugs(user: any): string[] {
        if (Array.isArray(user?.roleSlugs) && user.roleSlugs.length) {
            return user.roleSlugs.map((s: any) => String(s).toUpperCase()).filter(Boolean);
        }
        if (Array.isArray(user?.roles) && user.roles.length) {
            return user.roles.map((r: any) => String(r.slug ?? r.name ?? r.id ?? r).toUpperCase()).filter(Boolean);
        }
        if (user?.roleSlug) return [String(user.roleSlug).toUpperCase()];
        if (user?.role?.slug ?? user?.role?.name) return [String(user.role.slug ?? user.role.name).toUpperCase()];
        if (user?.roleId != null) {
            const found = this._roleById(user.roleId);
            if (found?.slug) return [String(found.slug).toUpperCase()];
        }
        return [];
    }

    private _extractRoleIds(user: any): number[] {
        if (Array.isArray(user?.roleIds) && user.roleIds.length) {
            return user.roleIds.map(Number).filter((n) => Number.isFinite(n));
        }
        if (Array.isArray(user?.roles) && user.roles.length) {
            return user.roles.map((r: any) => Number(r.id ?? this._roleById(r.slug ?? r.name)?.id)).filter((n: number) => Number.isFinite(n));
        }
        if (user?.roleId != null) return [Number(user.roleId)];
        return [];
    }

    closeUserDetail() {
        this.showEntityFormPanel = false;
        this.md.back(this.md.list(this.basePath));
    }

    toggleNewUserModal() {
        this.selectedUser = { isNew: true, entities: [], roleIds: [], partyUuid: null, expiredDate: this.maxDateStr, isActive: true };
        this.mode.set(FormMode.CREATE);
        this.userForm.reset({ isActive: true, roleIds: [], partyUuid: null });
        this.userForm.patchValue(this.selectedUser);
        this._updateAvailableParties();
        this.open(this.newUser);
    }

    toggleEntityModal() {
        if (this.appSettings.isMobile()) {
            this.showEntityFormPanel = true;
            return;
        }
        this.open(this.entityModal);
    }

    closeEntityFormPanel() {
        this.showEntityFormPanel = false;
        this.close();
    }

    saveRoleEntitiesFromPanel() {
        const form = this.entitiesFormPanel;
        if (form) this.saveRoleEntitiesFromModal(form);
    }

    getAllEntities(): any[] {
        return this.activeEntities();
    }

    getFilteredEntities(): any[] {
        return this.assignableEntities();
    }

    saveWithEntities(userFormComp: any, entitiesFormComp: any) {
        if (!this.userForm.valid) return;
        const codes = this._extractSelectedCodes(entitiesFormComp);
        if (this.selectedUser?.isNew) {
            this._createWithEntities(this.userForm.value, codes);
            return;
        }
        this._updateExistingEntities(codes);
    }

    private _extractSelectedCodes(comp: any): number[] {
        const list = comp?.formUser?.get('entities')?.value ?? [];
        return list.map((e: any) => Number(typeof e === 'object' ? e.code : e)).filter(Number.isFinite);
    }

    private _createWithEntities(val: any, codes: number[]) {
        const roleSlugs = this._formRoleSlugs(val);
        const roleSlug = val.roleSlug && roleSlugs.includes(val.roleSlug) ? val.roleSlug : (roleSlugs[0] ?? null);
        const payload = { ...val, roleSlugs, roleSlug, entityCodes: codes };
        this.userService.create(payload).subscribe({
            next: (res: any) => this._onCreateSuccess(res, roleSlug, codes),
            error: () => this.showError('No se pudo crear el usuario base')
        });
    }

    private _formRoleSlugs(val: any): string[] {
        const raw = val.roleSlugs?.length ? val.roleSlugs : val.roleIds;
        return Array.isArray(raw) ? raw.map((s) => String(s).toUpperCase()) : [];
    }

    private _onCreateSuccess(res: any, fallbackSlug: string, codes: number[]) {
        this._applyUserResponse(res);
        const slug = res.roleSlug ?? fallbackSlug;
        if (!codes.length || !res?.uuid || !slug) {
            this._finishCreate('Usuario creado');
            return;
        }
        this.userService.saveRoleEntities(res.uuid, slug, codes).subscribe({
            next: (upd: any) => this._finishCreate('Usuario y entidades guardados', upd),
            error: () => this.showError('Usuario creado pero falló asignación de entidades')
        });
    }

    private _finishCreate(msg: string, updatedUser?: any) {
        if (updatedUser) this._applyUserResponse(updatedUser);
        this.showSuccess(msg);
        this.userList.loadUsers();
        this.close();
    }

    private _updateExistingEntities(codes: number[]) {
        const target = this.entitiesRoleSlug ?? this.entitiesRoleId;
        if (!this.selectedUser?.uuid || !target) return;
        this._persistRoleEntities(target, codes, () => this.close());
    }

    private showSuccess(title: string) {
        Swal.fire({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            icon: 'success',
            title
        });
    }

    private showError(title: string) {
        Swal.fire({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            icon: 'error',
            title
        });
    }

    handleFormChange(formValue: any) {
        if (formValue === null) {
            this.userList.loadUsers();
            this.md.go(this.md.list(this.basePath));
            return;
        }
        const data = this._normalizeFormData(formValue.value ?? formValue);
        this.mode() === FormMode.EDIT ? this.updateUser(data) : this.createUser(data);
    }

    private _normalizeFormData(raw: any): any {
        const data = { ...raw };
        const roleSlugs = this._formRoleSlugs(data);
        data.roleSlugs = roleSlugs;
        data.roleSlug = data.roleSlug && roleSlugs.includes(data.roleSlug) ? data.roleSlug : (roleSlugs[0] ?? null);
        if (!this.hasUserRole(roleSlugs)) data.partyUuid = null;
        return data;
    }

    private updateUser(data: any) {
        if (!this.selectedUser?.uuid) return;
        this.userService.update({ id: this.selectedUser.uuid, ...data }).subscribe({
            next: (res: any) => {
                this.showToast('Usuario actualizado correctamente', 'success');
                this.userList.loadUsers();
                this.mode.set(FormMode.VIEW);
                this._applyUserResponse(res);
            },
            error: (err) => this.showToast(err?.error?.error ?? err?.error?.message ?? 'No se pudo actualizar el usuario', 'error')
        });
    }

    private createUser(data: any) {
        this.userService.create(data).subscribe({
            next: (res: any) => {
                this.showToast('Usuario creado correctamente', 'success');
                this.userList.loadUsers();
                this.close();
                if (res?.uuid) {
                    this.applyUser(res);
                    this.md.go(this.md.item(this.basePath, res.uuid));
                }
            },
            error: (err) => this.showToast(err?.error?.error ?? err?.error?.message ?? 'No se pudo crear el usuario', 'error')
        });
    }

    showToast(title: string, icon: 'success' | 'error') {
        Swal.fire({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            icon,
            title
        });
    }

    confirmDeletion(callback: () => void) {
        Swal.fire({
            title: '¿Estás seguro?',
            text: "Se eliminará la asociación con esta entidad",
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: 'Cancelar',
            confirmButtonText: 'Sí, eliminar',
        }).then((res) => res.isConfirmed && callback());
    }

    getIds(items: any[]): number[] {
        return (items || []).map(i => i.id);
    }

}
